import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, createPart, createPlaylist, createTrack, deletePart, deleteTrack, getEventQR } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Plus, Trash2, QrCode, ArrowLeft } from 'lucide-react';
import UploadProgressModal from '../components/UploadProgressModal';
import ConfirmDialog from '../components/ConfirmDialog';
import DuplicateWarningDialog from '../components/DuplicateWarningDialog';
import LocalFileBrowser from '../components/LocalFileBrowser';
import api from '../utils/api';
import EventEditorHeader from '../components/eventeditor/EventEditorHeader';
import PlaylistManager from '../components/eventeditor/PlaylistManager';
import OfflineManager from '../components/OfflineManager';
import { formatModeLabel } from '../utils/formatters';
import LoadingScreen from '../components/ui/LoadingScreen';

const EventEditor = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [trackDialogOpen, setTrackDialogOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Local file browser state
  const [localBrowserOpen, setLocalBrowserOpen] = useState(false);
  const [localBrowserPlaylistId, setLocalBrowserPlaylistId] = useState(null);
  
  // Duplicate detection state
  const [duplicateWarning, setDuplicateWarning] = useState({
    isOpen: false,
    duplicates: [],
    pendingFile: null,
    pendingPlaylistId: null,
    pendingIndex: 0
  });
  const [pendingUploads, setPendingUploads] = useState([]);
  
  const [partForm, setPartForm] = useState({
    name: '',
    mode: 'auto',
    estimated_duration_minutes: 30
  });
  const [trackForm, setTrackForm] = useState({
    title: '',
    artist: '',
    source_type: 'url',
    source_url: ''
  });

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  const loadEvent = async () => {
    try {
      const response = await getEvent(eventId);
      setEvent(response.data);
      
      // Load playlists for each part
      const partsWithPlaylists = await Promise.all(
        response.data.parts.map(async (part) => {
          try {
            const playlistsResponse = await api.get(`/parts/${part.id}`);
            return { ...part, playlists: playlistsResponse.data.playlists || [] };
          } catch (error) {
            return { ...part, playlists: [] };
          }
        })
      );
      
      setEvent({ ...response.data, parts: partsWithPlaylists });
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePart = async (e) => {
    e.preventDefault();
    try {
      const orderIndex = event.parts.length;
      await createPart({ ...partForm, event_id: eventId, order_index: orderIndex });
      
      toast.success('Partie créée avec succès !');
      setPartDialogOpen(false);
      setPartForm({ name: '', mode: 'auto', estimated_duration_minutes: 30 });
      loadEvent();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const [deletePartId, setDeletePartId] = useState(null);

  const handleDeletePart = async () => {
    if (!deletePartId) return;
    try {
      await deletePart(deletePartId);
      toast.success('Partie supprimée');
      setDeletePartId(null);
      loadEvent();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreatePlaylist = async (partId, type) => {
    try {
      await createPlaylist({
        part_id: partId,
        name: `${type === 'main' ? 'Principale' : 'Secours'}`,
        type
      });
      toast.success('Playlist créée');
      loadEvent();
    } catch (error) {
      toast.error('Erreur lors de la création de la playlist');
    }
  };

  const handleAddTrack = async (e) => {
    e.preventDefault();
    if (!selectedPlaylist) return;
    
    try {
      await createTrack({
        ...trackForm,
        playlist_id: selectedPlaylist.id,
        order_index: selectedPlaylist.tracks?.length || 0
      });
      toast.success('Titre ajouté !');
      setTrackDialogOpen(false);
      setTrackForm({ title: '', artist: '', source_type: 'url', source_url: '' });
      loadEvent();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleUploadTrack = async (e, playlistId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    console.log(`📤 Starting upload of ${files.length} file(s)`);
    
    // Store files for potential duplicate handling
    setPendingUploads(files.map((file, index) => ({
      file,
      playlistId,
      index,
      skipDuplicateCheck: false
    })));
    
    setUploading(true);
    setShowUploadModal(true);
    
    // Initialize progress tracking
    const progressData = files.map(file => ({
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'pending' // pending, checking, uploading, completed, error, duplicate
    }));
    setUploadProgress(progressData);
    
    console.log('📊 Initial progress data:', progressData);
    
    try {
      // Process files one by one
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const title = file.name.replace(/\.[^/.]+$/, '');
        
        console.log(`🔍 Checking for duplicates: ${file.name}`);
        
        // Update status to checking
        setUploadProgress(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'checking' } : item
        ));
        
        // Check for duplicates
        try {
          // Use JSON - cleaner and lighter than FormData
          const dupResponse = await api.post('/tracks/check-duplicate', {
            event_id: eventId,
            title: title,
            filename: file.name
          });
          
          if (dupResponse.data.is_duplicate && dupResponse.data.duplicates.length > 0) {
            console.log(`⚠️ Duplicate found for ${file.name}:`, dupResponse.data.duplicates);
            
            // Show duplicate warning dialog
            setDuplicateWarning({
              isOpen: true,
              duplicates: dupResponse.data.duplicates,
              pendingFile: file,
              pendingPlaylistId: playlistId,
              pendingIndex: i
            });
            
            // Mark as duplicate (will be resolved by user action)
            setUploadProgress(prev => prev.map((item, idx) => 
              idx === i ? { ...item, status: 'duplicate' } : item
            ));
            
            // Wait for user decision before continuing
            return;
          }
        } catch (dupError) {
          console.log('Duplicate check failed, proceeding with upload:', dupError);
          // Continue with upload if duplicate check fails
        }
        
        // Proceed with upload
        await uploadSingleFile(file, playlistId, i);
      }
      
      // Finalize upload
      finalizeUpload();
    } catch (error) {
      console.error('💥 Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  
  // Helper function to upload a single file
  const uploadSingleFile = async (file, playlistId, index) => {
    console.log(`⬆️ Uploading file: ${file.name}`);
    
    setUploadProgress(prev => prev.map((item, idx) => 
      idx === index ? { ...item, status: 'uploading' } : item
    ));
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('playlist_id', playlistId);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('artist', 'Unknown');
    formData.append('order_index', index);

    try {
      await api.post('/tracks/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(prev => prev.map((item, idx) => 
            idx === index ? { ...item, progress: percentCompleted } : item
          ));
        }
      });
      
      console.log(`✅ File ${file.name} uploaded successfully`);
      
      setUploadProgress(prev => prev.map((item, idx) => 
        idx === index ? { ...item, status: 'completed', progress: 100 } : item
      ));
      
      return true;
    } catch (uploadError) {
      console.error(`❌ Upload error for ${file.name}:`, uploadError);
      setUploadProgress(prev => prev.map((item, idx) => 
        idx === index ? { 
          ...item, 
          status: 'error', 
          error: uploadError.response?.data?.detail || 'Erreur d\'upload'
        } : item
      ));
      return false;
    }
  };
  
  // Handle duplicate confirmation (add anyway)
  const handleDuplicateConfirm = async () => {
    const { pendingFile, pendingPlaylistId, pendingIndex } = duplicateWarning;
    
    setDuplicateWarning(prev => ({ ...prev, isOpen: false }));
    
    // Upload the file anyway
    await uploadSingleFile(pendingFile, pendingPlaylistId, pendingIndex);
    
    // Continue with remaining files
    const remainingUploads = pendingUploads.slice(pendingIndex + 1);
    for (const upload of remainingUploads) {
      await uploadSingleFile(upload.file, upload.playlistId, upload.index);
    }
    
    finalizeUpload();
  };
  
  // Handle duplicate cancel (skip this file)
  const handleDuplicateCancel = async () => {
    const { pendingIndex } = duplicateWarning;
    
    setDuplicateWarning(prev => ({ ...prev, isOpen: false }));
    
    // Mark as skipped
    setUploadProgress(prev => prev.map((item, idx) => 
      idx === pendingIndex ? { ...item, status: 'skipped', error: 'Doublon ignoré' } : item
    ));
    
    // Continue with remaining files
    const remainingUploads = pendingUploads.slice(pendingIndex + 1);
    for (const upload of remainingUploads) {
      await uploadSingleFile(upload.file, upload.playlistId, upload.index);
    }
    
    finalizeUpload();
  };
  
  // Finalize upload process
  const finalizeUpload = () => {
    setTimeout(() => {
      setUploadProgress(prev => {
        const successCount = prev.filter(f => f.status === 'completed').length;
        const skippedCount = prev.filter(f => f.status === 'skipped').length;
        console.log(`🎯 Upload complete: ${successCount}/${prev.length} files succeeded, ${skippedCount} skipped`);
        
        if (successCount > 0) {
          toast.success(`${successCount} fichier${successCount > 1 ? 's' : ''} uploadé${successCount > 1 ? 's' : ''} !`);
          setTimeout(() => {
            loadEvent();
            setShowUploadModal(false);
            setUploadProgress([]);
            setPendingUploads([]);
          }, 2000);
        } else if (skippedCount === prev.length) {
          toast.info('Tous les fichiers étaient des doublons');
          setTimeout(() => {
            setShowUploadModal(false);
            setUploadProgress([]);
            setPendingUploads([]);
          }, 1500);
        }
        return prev;
      });
      setUploading(false);
    }, 300);
  };

  const [deleteTrackId, setDeleteTrackId] = useState(null);

  const handleDeleteTrack = async () => {
    if (!deleteTrackId) return;
    try {
      await deleteTrack(deleteTrackId);
      toast.success('Titre supprimé');
      setDeleteTrackId(null);
      loadEvent();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleShowQR = async () => {
    try {
      // Backend now returns QR code with new guest URL format
      const response = await getEventQR(eventId);
      setQrCode(response.data);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Erreur lors de la génération du QR code');
    }
  };

  if (loading) {
    return <LoadingScreen message="Chargement de l'événement..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header - Using EventEditorHeader component */}
        <EventEditorHeader event={event} onShowQR={handleShowQR} />

        {/* Offline Manager - Prepare event for offline use */}
        <div className="mb-4 sm:mb-6">
          <OfflineManager eventId={eventId} eventName={event.name} />
        </div>

        {/* Duplicate Warning Dialog */}
        <DuplicateWarningDialog
          isOpen={duplicateWarning.isOpen}
          onClose={() => setDuplicateWarning(prev => ({ ...prev, isOpen: false }))}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
          duplicates={duplicateWarning.duplicates}
          uploadingFile={duplicateWarning.pendingFile?.name}
        />

        {/* QR Code Modal */}
        {qrCode && (
          <Dialog open={!!qrCode} onOpenChange={() => setQrCode(null)}>
            <DialogContent className="bg-white border-gray-200 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-xl text-gray-900">QR Code pour les invités</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4 p-6">
                <img src={qrCode.qr_code} alt="QR Code" className="w-64 h-64 rounded-lg" data-testid="qr-code-image" />
                <p className="text-gray-600 text-sm text-center">
                  Les invités peuvent scanner ce code pour proposer des musiques
                </p>
                <code className="bg-gray-100 px-4 py-2 rounded-lg text-violet-600 text-xs">{qrCode.url}</code>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Add Part Button */}
        <div className="mb-4 sm:mb-6">
          <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-part-button"
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white w-full sm:w-auto text-sm sm:text-base"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une partie
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-xl text-gray-900">Nouvelle partie</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePart} className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700 text-sm mb-2 block">Nom</Label>
                  <Input
                    data-testid="part-name-input"
                    value={partForm.name}
                    onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-violet-500 focus:ring-violet-500"
                    placeholder="Accueil, Dancefloor, etc."
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-700 text-sm mb-2 block">Mode</Label>
                  <Select value={partForm.mode} onValueChange={(value) => setPartForm({ ...partForm, mode: value })}>
                    <SelectTrigger data-testid="part-mode-select" className="bg-gray-50 border-gray-300 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="semi_auto">Semi-auto</SelectItem>
                      <SelectItem value="manual">Manuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="submit-part-button"
                  type="submit"
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
                >
                  Créer la partie
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Parts Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {event.parts?.map((part, index) => (
            <AccordionItem
              key={part.id}
              value={part.id}
              data-testid={`part-accordion-${part.id}`}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
            >
              <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 hover:no-underline">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-violet-600">#{index + 1}</span>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900">{part.name}</h3>
                      <p className="text-sm text-gray-500">Mode: {formatModeLabel(part.mode)}</p>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-6">
                  {/* Playlists - Using PlaylistManager component */}
                  {!part.playlists || part.playlists.length === 0 ? (
                    <div className="flex gap-3">
                      <Button
                        data-testid={`create-main-playlist-${part.id}`}
                        onClick={() => handleCreatePlaylist(part.id, 'main')}
                        variant="outline"
                        className="border-violet-300 text-violet-600 hover:bg-violet-50"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créer Playlist Principale
                      </Button>
                      <Button
                        data-testid={`create-backup-playlist-${part.id}`}
                        onClick={() => handleCreatePlaylist(part.id, 'backup')}
                        variant="outline"
                        className="border-violet-300 text-violet-600 hover:bg-violet-50"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créer Playlist Secours
                      </Button>
                    </div>
                  ) : (
                    part.playlists.map((playlist) => (
                      <PlaylistManager
                        key={playlist.id}
                        playlist={playlist}
                        onAddTrack={() => {
                          setSelectedPlaylist(playlist);
                          setTrackDialogOpen(true);
                        }}
                        onUpload={(files) => {
                          // Create a synthetic event object for handleUploadTrack
                          const syntheticEvent = {
                            target: {
                              files: files,
                              value: ''
                            }
                          };
                          handleUploadTrack(syntheticEvent, playlist.id);
                        }}
                        onOpenLocalBrowser={() => {
                          setLocalBrowserPlaylistId(playlist.id);
                          setLocalBrowserOpen(true);
                        }}
                        uploading={uploading}
                      />
                    ))
                  )}

                  {/* Delete Part */}
                  <Button
                    data-testid={`delete-part-${part.id}`}
                    onClick={() => setDeletePartId(part.id)}
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer cette partie
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Delete Part Confirmation */}
        <ConfirmDialog
          open={!!deletePartId}
          onOpenChange={() => setDeletePartId(null)}
          onConfirm={handleDeletePart}
          title="Supprimer cette partie ?"
          description="Toutes les playlists et pistes associées seront également supprimées. Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          variant="destructive"
          icon={Trash2}
        />

        {/* Delete Track Confirmation */}
        <ConfirmDialog
          open={!!deleteTrackId}
          onOpenChange={() => setDeleteTrackId(null)}
          onConfirm={handleDeleteTrack}
          title="Supprimer ce titre ?"
          description="Le fichier audio sera définitivement supprimé. Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          variant="destructive"
          icon={Trash2}
        />

        {/* Upload Progress Modal */}
        <UploadProgressModal
          open={showUploadModal}
          files={uploadProgress}
          onOpenChange={setShowUploadModal}
        />

        {/* Local File Browser Modal */}
        <LocalFileBrowser
          isOpen={localBrowserOpen}
          onClose={() => {
            setLocalBrowserOpen(false);
            setLocalBrowserPlaylistId(null);
          }}
          playlistId={localBrowserPlaylistId}
          eventId={eventId}
          onUploadComplete={loadEvent}
        />

        {/* Add Track Dialog */}
        <Dialog open={trackDialogOpen} onOpenChange={setTrackDialogOpen}>
          <DialogContent className="bg-white border-gray-200 text-gray-900">
            <DialogHeader>
              <DialogTitle className="text-xl text-gray-900">Ajouter un titre</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddTrack} className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-700 text-sm mb-2 block">Titre</Label>
                <Input
                  data-testid="track-title-input"
                  value={trackForm.title}
                  onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })}
                  className="bg-gray-50 border-gray-300 text-gray-900 focus:border-violet-500"
                  placeholder="Nom du titre"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-700 text-sm mb-2 block">Artiste</Label>
                <Input
                  data-testid="track-artist-input"
                  value={trackForm.artist}
                  onChange={(e) => setTrackForm({ ...trackForm, artist: e.target.value })}
                  className="bg-gray-50 border-gray-300 text-gray-900 focus:border-violet-500"
                  placeholder="Nom de l'artiste"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-700 text-sm mb-2 block">URL (optionnel)</Label>
                <Input
                  data-testid="track-url-input"
                  value={trackForm.source_url}
                  onChange={(e) => setTrackForm({ ...trackForm, source_url: e.target.value })}
                  className="bg-gray-50 border-gray-300 text-gray-900 focus:border-violet-500"
                  placeholder="https://..."
                />
              </div>
              <Button
                data-testid="submit-track-button"
                type="submit"
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
              >
                Ajouter le titre
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Internal components removed - now in separate files

export default EventEditor;
